import * as THREE from 'three';

export const LEGACY_PET_POINT_EFFECT_SECONDS = 10;
export const LEGACY_PET_HEART_BURST_SECONDS = 10;
const LEGACY_TO_BROWSER_WORLD_SCALE = 0.1;
const POINT_TEXTURE_SIZE = 64;

export type LegacyPetPointEmitter = Readonly<{
  sourceEntry: string;
  particleCount: number;
  startSize: number;
  endSize: number;
  startColor: readonly [number, number, number, number];
  endColor: readonly [number, number, number, number];
  blending: 'additive' | 'source-over';
  initialVelocity: number;
  minimumLifetimeMilliseconds: number;
  maximumLifetimeMilliseconds: number;
  minimumAngleRadians: number;
  maximumAngleRadians: number;
  emissionDirection: readonly [number, number, number];
  releaseRatePerSecond: number;
  localScale: number;
  ringInnerRadius: number;
  ringOuterRadius: number;
  controllerPrecisionSeconds: number;
}>;

export type LegacyPetPointParticleConfig = Readonly<{
  schemaVersion: 1;
  emitters: Readonly<{
    pointText: LegacyPetPointEmitter;
    yellowStars: LegacyPetPointEmitter;
    whiteStars: LegacyPetPointEmitter;
  }>;
}>;

export type LegacyPetPointParticleFrame = Readonly<{
  active: boolean;
  opacity: number;
  size: number;
  position: THREE.Vector3;
}>;

export type LegacyPetProgressionSnapshot = Readonly<{
  petLevel: number;
  petPointsWithinLevel: number;
}>;

const PET_LEVEL_POINT_THRESHOLDS = Object.freeze([10, 20, 40, 80, 160, 320] as const);

type ParticleSprite = Readonly<{
  sprite: THREE.Sprite;
  material: THREE.SpriteMaterial;
  emitter: LegacyPetPointEmitter;
  releaseSeconds: number;
  lifetimeMilliseconds: number;
  origin: THREE.Vector3;
  velocity: THREE.Vector3;
}>;

type RandomSource = () => number;

/** Strictly validates the deterministic projection of the two serialized JME emitters. */
export function parseLegacyPetPointParticleConfig(value: unknown): LegacyPetPointParticleConfig {
  const root = record(value, 'pet point particle config');
  if (root.schemaVersion !== 1) throw new Error('Unsupported pet point particle schema');
  const emitters = record(root.emitters, 'pet point particle emitters');
  return {
    schemaVersion: 1,
    emitters: {
      pointText: parseEmitter(emitters.pointText, 'pointText', 1),
      yellowStars: parseEmitter(emitters.yellowStars, 'yellowStars', 20),
      whiteStars: parseEmitter(emitters.whiteStars, 'whiteStars', 20),
    },
  };
}

/** JME updates position in milliseconds and applies the emitter's local scale at render time. */
export function legacyPetPointParticleFrame(
  emitter: LegacyPetPointEmitter,
  elapsedSeconds: number,
  releaseSeconds: number,
  lifetimeMilliseconds: number,
  origin: THREE.Vector3,
  velocity: THREE.Vector3,
): LegacyPetPointParticleFrame {
  const ageMilliseconds = (Math.max(0, elapsedSeconds) - releaseSeconds) * 1_000;
  if (ageMilliseconds < 0 || ageMilliseconds > lifetimeMilliseconds) {
    return { active: false, opacity: 0, size: 0, position: origin.clone() };
  }
  const progress = Math.min(1, ageMilliseconds / lifetimeMilliseconds);
  const spatialScale = emitter.localScale * LEGACY_TO_BROWSER_WORLD_SCALE;
  return {
    active: true,
    opacity: THREE.MathUtils.lerp(emitter.startColor[3], emitter.endColor[3], progress),
    size: THREE.MathUtils.lerp(emitter.startSize, emitter.endSize, progress) * spatialScale,
    position: origin.clone().addScaledVector(velocity, ageMilliseconds * spatialScale),
  };
}

/** Resolves an event-41 observer's branch even if avatar-data delivery trails the event. */
export function legacyPetPointAwardStatus(
  pointsGained: number,
  previous: LegacyPetProgressionSnapshot | undefined,
  current: LegacyPetProgressionSnapshot | undefined,
): 'success' | 'level-up' {
  if (!Number.isInteger(pointsGained) || pointsGained <= 0) return 'success';
  if (previous !== undefined && previous.petLevel >= 1 && previous.petLevel < 7) {
    const threshold = PET_LEVEL_POINT_THRESHOLDS[previous.petLevel - 1];
    if (threshold !== undefined && previous.petPointsWithinLevel + pointsGained >= threshold) {
      return 'level-up';
    }
  }
  if (
    previous !== undefined &&
    current !== undefined &&
    (current.petLevel > previous.petLevel ||
      current.petPointsWithinLevel < previous.petPointsWithinLevel)
  ) {
    return 'level-up';
  }
  if (previous === undefined && current !== undefined) {
    if (current.petLevel > 1 && current.petPointsWithinLevel === 0) return 'level-up';
  }
  return 'success';
}

export class LegacyPetPointEffect {
  readonly root = new THREE.Group();
  private readonly particles: ParticleSprite[];
  private elapsedSeconds = 0;
  private disposed = false;

  constructor(
    config: LegacyPetPointParticleConfig,
    pointTexture: THREE.Texture,
    yellowStarTexture: THREE.Texture,
    targetPosition: THREE.Vector3,
    verticalOffset: number,
    random: RandomSource = Math.random,
  ) {
    this.root.name = 'pet-point-feedback';
    this.root.position.copy(targetPosition);
    const text = createParticleSprite(
      config.emitters.pointText,
      pointTexture,
      0,
      random,
      'pet-points-text',
    );
    const stars = Array.from({ length: config.emitters.yellowStars.particleCount }, (_, index) =>
      createParticleSprite(
        config.emitters.yellowStars,
        yellowStarTexture,
        index,
        random,
        `pet-points-yellow-star-${String(index + 1)}`,
      ),
    );
    this.particles = [text, ...stars];
    for (const particle of this.particles) {
      particle.sprite.position.y = verticalOffset;
      particle.sprite.userData.verticalOffset = verticalOffset;
      particle.sprite.visible = false;
      this.root.add(particle.sprite);
    }
  }

  update(deltaSeconds: number): boolean {
    if (this.disposed) return false;
    this.elapsedSeconds += Math.max(0, deltaSeconds);
    for (const particle of this.particles) {
      const frame = legacyPetPointParticleFrame(
        particle.emitter,
        this.elapsedSeconds,
        particle.releaseSeconds,
        particle.lifetimeMilliseconds,
        particle.origin,
        particle.velocity,
      );
      particle.sprite.visible = frame.active && frame.opacity > 0;
      particle.material.opacity = frame.opacity;
      particle.sprite.scale.set(frame.size, frame.size, 1);
      const verticalOffset = particle.sprite.userData.verticalOffset as number;
      particle.sprite.position.copy(frame.position);
      particle.sprite.position.y += verticalOffset;
    }
    if (this.elapsedSeconds >= LEGACY_PET_POINT_EFFECT_SECONDS) {
      this.dispose();
      return false;
    }
    return true;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const particle of this.particles) particle.material.dispose();
    const pointTexture = this.particles[0]?.material.map;
    pointTexture?.dispose();
    this.root.removeFromParent();
  }
}

/**
 * `VP.HEARTBURST`: the original Follow/equip feedback. It reuses the serialized
 * billboard-burst emitter with the pet-specific heart texture, stays anchored at the pet's
 * equip position, and retains its particle node for the source's full ten seconds.
 */
export class LegacyPetHeartBurstEffect {
  readonly root = new THREE.Group();
  private readonly particles: ParticleSprite[];
  private elapsedSeconds = 0;
  private disposed = false;

  constructor(
    config: LegacyPetPointParticleConfig,
    heartTexture: THREE.Texture,
    targetPosition: THREE.Vector3,
    verticalOffset: number,
    random: RandomSource = Math.random,
  ) {
    const emitter = config.emitters.yellowStars;
    this.root.name = 'pet-equip-heart-burst';
    this.root.position.copy(targetPosition);
    this.particles = Array.from({ length: emitter.particleCount }, (_, index) =>
      createParticleSprite(
        emitter,
        heartTexture,
        index,
        random,
        `pet-equip-heart-${String(index + 1)}`,
      ),
    );
    for (const particle of this.particles) {
      particle.sprite.position.y = verticalOffset;
      particle.sprite.userData.verticalOffset = verticalOffset;
      particle.sprite.visible = false;
      this.root.add(particle.sprite);
    }
  }

  update(deltaSeconds: number): boolean {
    if (this.disposed) return false;
    this.elapsedSeconds += Math.max(0, deltaSeconds);
    for (const particle of this.particles) {
      const frame = legacyPetPointParticleFrame(
        particle.emitter,
        this.elapsedSeconds,
        particle.releaseSeconds,
        particle.lifetimeMilliseconds,
        particle.origin,
        particle.velocity,
      );
      particle.sprite.visible = frame.active && frame.opacity > 0;
      particle.material.opacity = frame.opacity;
      particle.sprite.scale.set(frame.size, frame.size, 1);
      const verticalOffset = particle.sprite.userData.verticalOffset as number;
      particle.sprite.position.copy(frame.position);
      particle.sprite.position.y += verticalOffset;
    }
    if (this.elapsedSeconds >= LEGACY_PET_HEART_BURST_SECONDS) {
      this.dispose();
      return false;
    }
    return true;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const particle of this.particles) particle.material.dispose();
    this.root.removeFromParent();
  }
}

/**
 * `VP.WHITESTARS`: the same serialized burst controller used by the original building-ready
 * transition, with its distinct doubled local scale and white-star texture.
 */
export class LegacyWhiteStarBurstEffect {
  readonly root = new THREE.Group();
  private readonly particles: ParticleSprite[];
  private readonly durationSeconds: number;
  private elapsedSeconds = 0;
  private disposed = false;

  constructor(
    config: LegacyPetPointParticleConfig,
    whiteStarTexture: THREE.Texture,
    targetPosition: THREE.Vector3,
    random: RandomSource = Math.random,
    identity: Readonly<{
      rootName: string;
      particleNamePrefix: string;
    }> = {
      rootName: 'building-ready-white-stars',
      particleNamePrefix: 'building-ready-white-star',
    },
  ) {
    const emitter = config.emitters.whiteStars;
    this.root.name = identity.rootName;
    this.root.position.copy(targetPosition);
    this.particles = Array.from({ length: emitter.particleCount }, (_, index) =>
      createParticleSprite(
        emitter,
        whiteStarTexture,
        index,
        random,
        `${identity.particleNamePrefix}-${String(index + 1)}`,
      ),
    );
    this.durationSeconds =
      emitter.particleCount / emitter.releaseRatePerSecond +
      emitter.maximumLifetimeMilliseconds / 1_000;
    for (const particle of this.particles) {
      particle.sprite.visible = false;
      this.root.add(particle.sprite);
    }
  }

  update(deltaSeconds: number): boolean {
    if (this.disposed) return false;
    this.elapsedSeconds += Math.max(0, deltaSeconds);
    for (const particle of this.particles) {
      const frame = legacyPetPointParticleFrame(
        particle.emitter,
        this.elapsedSeconds,
        particle.releaseSeconds,
        particle.lifetimeMilliseconds,
        particle.origin,
        particle.velocity,
      );
      particle.sprite.visible = frame.active && frame.opacity > 0;
      particle.material.opacity = frame.opacity;
      particle.sprite.scale.set(frame.size, frame.size, 1);
      particle.sprite.position.copy(frame.position);
    }
    if (this.elapsedSeconds >= this.durationSeconds) {
      this.dispose();
      return false;
    }
    return true;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const particle of this.particles) particle.material.dispose();
    this.root.removeFromParent();
  }
}

export function createLegacyPetPointTexture(pointsGained: number): THREE.CanvasTexture {
  if (!Number.isInteger(pointsGained) || pointsGained <= 0) {
    throw new Error('Pet point feedback requires a positive integer');
  }
  const canvas = document.createElement('canvas');
  canvas.width = POINT_TEXTURE_SIZE;
  canvas.height = POINT_TEXTURE_SIZE;
  const context = canvas.getContext('2d');
  if (context === null) throw new Error('Pet point feedback canvas is unavailable');
  const text = `+${String(pointsGained)}`;
  const fontSize = chooseLegacyPetPointFontSize(context, text);
  context.font = `${String(fontSize)}px "Legacy Baramond Bold"`;
  context.textBaseline = 'alphabetic';
  context.textAlign = 'left';
  context.imageSmoothingEnabled = true;
  const metrics = context.measureText(text);
  const ascent = Math.ceil(metrics.actualBoundingBoxAscent);
  context.fillStyle = '#000000';
  context.fillText(text, 3, 3 + ascent);
  context.fillStyle = '#74aacd';
  context.fillText(text, 1, 1 + ascent);
  context.fillStyle = '#ffffff';
  context.fillText(text, 0, ascent);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  return texture;
}

export function chooseLegacyPetPointFontSize(
  context: CanvasRenderingContext2D,
  text: string,
): number {
  for (let size = 69; size >= 1; size -= 1) {
    context.font = `${String(size)}px "Legacy Baramond Bold"`;
    const metrics = context.measureText(text);
    const height =
      optionalMetric(metrics, 'fontBoundingBoxAscent', metrics.actualBoundingBoxAscent) +
      optionalMetric(metrics, 'fontBoundingBoxDescent', metrics.actualBoundingBoxDescent);
    if (metrics.width < 60 && height < 60) return size;
  }
  return 1;
}

function createParticleSprite(
  emitter: LegacyPetPointEmitter,
  texture: THREE.Texture,
  index: number,
  random: RandomSource,
  name: string,
): ParticleSprite {
  const material = new THREE.SpriteMaterial({
    map: texture,
    color: new THREE.Color(emitter.startColor[0], emitter.startColor[1], emitter.startColor[2]),
    transparent: true,
    opacity: 0,
    depthTest: true,
    depthWrite: false,
    fog: false,
    toneMapped: false,
    alphaTest: Number.EPSILON,
    blending: THREE.CustomBlending,
    blendEquation: THREE.AddEquation,
    blendSrc: THREE.SrcAlphaFactor,
    blendDst: emitter.blending === 'additive' ? THREE.OneFactor : THREE.OneMinusSrcAlphaFactor,
  });
  const sprite = new THREE.Sprite(material);
  sprite.name = name;
  sprite.renderOrder = 2;
  const releaseSeconds = (index + 1) / emitter.releaseRatePerSecond;
  const lifetimeMilliseconds = THREE.MathUtils.lerp(
    emitter.minimumLifetimeMilliseconds,
    emitter.maximumLifetimeMilliseconds,
    boundedRandom(random),
  );
  const origin = randomRingPosition(emitter, random);
  const velocity = randomConeVelocity(emitter, random);
  return {
    sprite,
    material,
    emitter,
    releaseSeconds,
    lifetimeMilliseconds,
    origin,
    velocity,
  };
}

function randomRingPosition(emitter: LegacyPetPointEmitter, random: RandomSource): THREE.Vector3 {
  const innerSquared = emitter.ringInnerRadius * emitter.ringInnerRadius;
  const outerSquared = emitter.ringOuterRadius * emitter.ringOuterRadius;
  const radius = Math.sqrt(THREE.MathUtils.lerp(innerSquared, outerSquared, boundedRandom(random)));
  const angle = boundedRandom(random) * Math.PI * 2;
  const spatialScale = emitter.localScale * LEGACY_TO_BROWSER_WORLD_SCALE;
  return new THREE.Vector3(-radius * Math.sin(angle), 0, -radius * Math.cos(angle)).multiplyScalar(
    spatialScale,
  );
}

function randomConeVelocity(emitter: LegacyPetPointEmitter, random: RandomSource): THREE.Vector3 {
  const azimuth = boundedRandom(random) * Math.PI * 2;
  const angle = THREE.MathUtils.lerp(
    emitter.minimumAngleRadians,
    emitter.maximumAngleRadians,
    boundedRandom(random),
  );
  const axis = new THREE.Vector3(...emitter.emissionDirection).normalize();
  const tangent = new THREE.Vector3(1, 0, 0);
  if (Math.abs(axis.dot(tangent)) > 0.999) tangent.set(0, 0, 1);
  tangent.addScaledVector(axis, -axis.dot(tangent)).normalize();
  const bitangent = new THREE.Vector3().crossVectors(axis, tangent).normalize();
  return axis
    .multiplyScalar(Math.cos(angle))
    .addScaledVector(tangent, Math.cos(azimuth) * Math.sin(angle))
    .addScaledVector(bitangent, Math.sin(azimuth) * Math.sin(angle))
    .multiplyScalar(emitter.initialVelocity);
}

function parseEmitter(
  value: unknown,
  label: string,
  expectedParticles: number,
): LegacyPetPointEmitter {
  const emitter = record(value, label);
  const sourceEntry = stringValue(emitter.sourceEntry, `${label}.sourceEntry`);
  const particleCount = integer(emitter.particleCount, `${label}.particleCount`, 1, 20);
  if (particleCount !== expectedParticles)
    throw new Error(`${label}.particleCount is incompatible`);
  const minimumLifetimeMilliseconds = finite(
    emitter.minimumLifetimeMilliseconds,
    `${label}.minimumLifetimeMilliseconds`,
  );
  const maximumLifetimeMilliseconds = finite(
    emitter.maximumLifetimeMilliseconds,
    `${label}.maximumLifetimeMilliseconds`,
  );
  if (
    minimumLifetimeMilliseconds <= 0 ||
    maximumLifetimeMilliseconds < minimumLifetimeMilliseconds
  ) {
    throw new Error(`${label} has invalid particle lifetimes`);
  }
  const releaseRatePerSecond = integer(
    emitter.releaseRatePerSecond,
    `${label}.releaseRatePerSecond`,
    1,
    100,
  );
  return {
    sourceEntry,
    particleCount,
    startSize: positive(emitter.startSize, `${label}.startSize`),
    endSize: positive(emitter.endSize, `${label}.endSize`),
    startColor: tuple(emitter.startColor, `${label}.startColor`, 4),
    endColor: tuple(emitter.endColor, `${label}.endColor`, 4),
    blending: blending(emitter.blending, `${label}.blending`),
    initialVelocity: positive(emitter.initialVelocity, `${label}.initialVelocity`),
    minimumLifetimeMilliseconds,
    maximumLifetimeMilliseconds,
    minimumAngleRadians: nonnegative(emitter.minimumAngleRadians, `${label}.minimumAngleRadians`),
    maximumAngleRadians: nonnegative(emitter.maximumAngleRadians, `${label}.maximumAngleRadians`),
    emissionDirection: tuple(emitter.emissionDirection, `${label}.emissionDirection`, 3),
    releaseRatePerSecond,
    localScale: positive(emitter.localScale, `${label}.localScale`),
    ringInnerRadius: nonnegative(emitter.ringInnerRadius, `${label}.ringInnerRadius`),
    ringOuterRadius: positive(emitter.ringOuterRadius, `${label}.ringOuterRadius`),
    controllerPrecisionSeconds: positive(
      emitter.controllerPrecisionSeconds,
      `${label}.controllerPrecisionSeconds`,
    ),
  };
}

function record(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`${label} must be an object`);
  }
  return value as Record<string, unknown>;
}

function stringValue(value: unknown, label: string): string {
  if (typeof value !== 'string' || value.length === 0) throw new Error(`${label} must be a string`);
  return value;
}

function finite(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${label} must be finite`);
  }
  return value;
}

function positive(value: unknown, label: string): number {
  const result = finite(value, label);
  if (result <= 0) throw new Error(`${label} must be positive`);
  return result;
}

function nonnegative(value: unknown, label: string): number {
  const result = finite(value, label);
  if (result < 0) throw new Error(`${label} must be nonnegative`);
  return result;
}

function blending(value: unknown, label: string): 'additive' | 'source-over' {
  if (value !== 'additive' && value !== 'source-over') {
    throw new Error(`${label} must be additive or source-over`);
  }
  return value;
}

function integer(value: unknown, label: string, minimum: number, maximum: number): number {
  const result = finite(value, label);
  if (!Number.isInteger(result) || result < minimum || result > maximum) {
    throw new Error(`${label} must be an integer from ${String(minimum)} to ${String(maximum)}`);
  }
  return result;
}

function tuple<const Length extends 3 | 4>(
  value: unknown,
  label: string,
  length: Length,
): Length extends 3
  ? readonly [number, number, number]
  : readonly [number, number, number, number] {
  if (!Array.isArray(value) || value.length !== length) throw new Error(`${label} is invalid`);
  const result = value.map((entry, index) => finite(entry, `${label}[${String(index)}]`));
  return result as never;
}

function optionalMetric(metrics: TextMetrics, name: string, fallback: number): number {
  const value = (metrics as unknown as Record<string, unknown>)[name];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function boundedRandom(random: RandomSource): number {
  return THREE.MathUtils.clamp(random(), 0, 1);
}
